import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full justify-between" }
const _hoisted_2 = {
  key: 0,
  class: "flex justify-center items-center h-full"
}
const _hoisted_3 = {
  key: 1,
  class: "space-y-6"
}
const _hoisted_4 = { class: "flex justify-between items-center mb-2 w-full" }
const _hoisted_5 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_6 = {
  key: 0,
  class: "flex justify-end gap-4"
}
const _hoisted_7 = { class: "mb-6" }
const _hoisted_8 = { class: "text-xl font-bold mb-2" }
const _hoisted_9 = { class: "text-sm text-gray-600" }
const _hoisted_10 = {
  key: 1,
  class: "flex flex-col items-center justify-center py-12 text-center"
}
const _hoisted_11 = {
  key: 1,
  class: "w-full text-center"
}
const _hoisted_12 = ["disabled"]
const _hoisted_13 = {
  key: 0,
  class: "flex items-center justify-center"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = {
  key: 2,
  disabled: "",
  class: "w-full opacity-50 cursor-not-allowed"
}

import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";
import { useNotifications } from "@/composables/useNotifications";
import { useTask } from "@/composables/tasks/useTask";
import { TasksService } from "@/services/v2/tasks";
import { useWeight } from "@/composables/tasks/vaccination/useVaccinationWeight";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { isLikelyBathVaccinationConfiguration } from "@/utils/v2/tasks/display";

import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import ConfigurationsTable from "@/components/v2/Tasks/Vaccination/Tables/ConfigurationsTable.vue";
import BathVaccinationConfigurationsTable from "@/components/v2/Tasks/Vaccination/Tables/BathVaccinationConfigurationsTable.vue";
import HistoryList from "@/components/v2/Tasks/Vaccination/Lists/HistoryList.vue";
import WeightModal from "@/components/v2/Tasks/Vaccination/Modals/WeightModal.vue";
import WeightIcon from "@/components/v2/UI/Icons/WeightIcon.vue";
import DocumentIcon from "@/components/v2/UI/Icons/DocumentIcon.vue";
import InfoIcon from "@/components/v2/UI/Icons/InfoIcon.vue";
import RandomWeightProgressCard from "@/components/v2/Tasks/Shared/RandomWeightProgressCard.vue";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================

export default /*@__PURE__*/_defineComponent({
  __name: 'ConfigurationsView',
  setup(__props) {

const router = useRouter();
const { activeTask } = useTask();
const { tanks } = useVaccination();
const { configurations } = useVaccinationConfigurations();
const { vaccineMethods } = useVaccination();
const { showTaskCompletionSuccess, showError } = useNotifications();
const {
  isIndividualWeightType,
  isNoWeightType,
  isRandomWeightType,
  getWeighedFish,
  getTargetWeightAmount
} = useWeight();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// Modal State
// ===================================================================
const selectedConfiguration = ref<any>(null);
const weightModal = ref<boolean>(false);

// ===================================================================
// Modal Handlers
// ===================================================================
const openWeightModal = (config?: any) => {
    weightModal.value = true;
    selectedConfiguration.value = config || null;
};

const goToEquipmentManagement = () => {
    router.push({ name: "VaccinationEquipmentView" });
};

// ===================================================================
// Computed Properties
// ===================================================================
// Simple loading state - consider task loaded when we have activeTask
const isLoading = computed(() => {
  return !activeTask.value;
});

// Detect if this is a bath vaccination task
const isBathTask = computed(() => {
  if (!configurations.value || configurations.value.length === 0) return false;

  return configurations.value.some(isLikelyBathVaccinationConfiguration);
});

// Detect if this is a simplified bath task (1 page workflow)
const isSimplifiedBathTask = computed(() => {
  if (!configurations.value || configurations.value.length === 0) return false;
  
  return configurations.value.some(config => {
    const bathConfig = config as any;
    return bathConfig.isSimplifiedBath === true;
  });
});

// Detect if this is a standard bath task (2 page workflow)
const isStandardBathTask = computed(() => {
  if (!configurations.value || configurations.value.length === 0) return false;
  
  return configurations.value.some(config => {
    const bathConfig = config as any;
    return bathConfig.isStandardBath === true;
  });
});

const taskTypeLabel = computed(() => {
  if (isSimplifiedBathTask.value) return 'Simplified Bath Vaccination';
  if (isStandardBathTask.value) return 'Standard Bath Vaccination';
  return 'Standard Vaccination';
});

const taskDescription = computed(() => {
  if (isSimplifiedBathTask.value) {
    return 'Review simplified bath vaccination configurations. Processes are time-based with specified durations. You can start and finish each bath process individually.';
  }
  if (isStandardBathTask.value) {
    return 'Review standard bath vaccination configurations. Processes are time-based with specified durations. You can start and finish each bath process individually.';
  }
  return 'You can make minor adjustments to tag colors, destination tanks, and destination groups if needed. All changes require justification before proceeding.';
});

// Check if configurations are empty
const hasConfigurations = computed(() => {
  return configurations.value && configurations.value.length > 0;
});
const completionValidation = computed(() => getWeightIdCompletionValidation());

// ===================================================================
// State
// ===================================================================
const instructionsModal = ref(false);
const isFinishing = ref(false);
const completionBlockModal = ref(false);

// ===================================================================
// Actions
// ===================================================================
// Open instructions modal
const toggleInstructionsModal = () => {
  instructionsModal.value = !instructionsModal.value;
};

// Next step
const handleProceed = () => {
  router.push({ name: 'VaccineVaccinations' });
};

// Handle finishing the task (for simplified bath)
const handleFinishTask = async () => {
  if (!activeTask.value) return;

  const validation = getWeightIdCompletionValidation();
  if (!validation.isValid) {
    completionBlockModal.value = true;
    return;
  }
  
  isFinishing.value = true;
  
  try {
    await TasksService.finishTask(activeTask.value);
    showTaskCompletionSuccess(activeTask.value.title);
  } catch (error) {
    console.error('Error completing vaccination task:', error);
    showError("Task Completion Failed", "Failed to complete the vaccination task. Please try again.");
  } finally {
    isFinishing.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[4] || (_cache[4] = [
          _createElementVNode("div", { class: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary" }, null, -1)
        ])))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", null, [
              _createElementVNode("h1", _hoisted_5, _toDisplayString(_unref(activeTask)?.title), 1)
            ]),
            (isBathTask.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("button", {
                    onClick: goToEquipmentManagement,
                    class: "px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm"
                  }, [
                    _createVNode(InfoIcon),
                    _cache[5] || (_cache[5] = _createElementVNode("span", null, "Equipment", -1))
                  ]),
                  (!_unref(isIndividualWeightType) && !_unref(isNoWeightType))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (openWeightModal())),
                        class: "px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm"
                      }, [
                        _createVNode(WeightIcon),
                        _cache[6] || (_cache[6] = _createTextVNode(" Record Weight "))
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).push({name: 'VaccineWeights'}))),
                    class: "px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm"
                  }, [
                    _createVNode(DocumentIcon),
                    _cache[7] || (_cache[7] = _createTextVNode(" View Weight Records "))
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          (_unref(isRandomWeightType))
            ? (_openBlock(), _createBlock(RandomWeightProgressCard, {
                key: 0,
                class: "sticky top-2 z-10",
                current: _unref(getWeighedFish),
                target: _unref(getTargetWeightAmount)
              }, null, 8, ["current", "target"]))
            : _createCommentVNode("", true),
          _cache[9] || (_cache[9] = _createElementVNode("span", { class: "w-full bg-secondary h-[1px] block" }, null, -1)),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("h2", _hoisted_8, _toDisplayString(taskTypeLabel.value), 1),
            _createElementVNode("p", _hoisted_9, _toDisplayString(taskDescription.value), 1)
          ]),
          _createVNode(HistoryList),
          (!hasConfigurations.value && !isLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[8] || (_cache[8] = [
                _createStaticVNode("<div class=\"w-16 h-16 mb-4 text-gray-400\"><svg fill=\"currentColor\" viewBox=\"0 0 20 20\" xmlns=\"http://www.w3.org/2000/svg\"><path fill-rule=\"evenodd\" d=\"M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z\" clip-rule=\"evenodd\"></path></svg></div><h3 class=\"text-lg font-medium text-gray-900 mb-2\">No Configurations Found</h3><p class=\"text-sm text-gray-500 max-w-md\"> Vaccination configurations are being prepared. Please wait while the system generates the required configurations for this task. </p>", 3)
              ])))
            : _createCommentVNode("", true),
          (!isBathTask.value && hasConfigurations.value && _unref(tanks)?.length)
            ? (_openBlock(), _createBlock(ConfigurationsTable, {
                key: 2,
                configurations: _unref(configurations),
                vaccineMethods: _unref(vaccineMethods),
                tanks: _unref(tanks)
              }, null, 8, ["configurations", "vaccineMethods", "tanks"]))
            : _createCommentVNode("", true),
          (isBathTask.value && hasConfigurations.value && _unref(tanks)?.length)
            ? (_openBlock(), _createBlock(BathVaccinationConfigurationsTable, {
                key: 3,
                configurations: _unref(configurations),
                vaccineMethods: _unref(vaccineMethods),
                tanks: _unref(tanks),
                onOpenWeightModal: openWeightModal
              }, null, 8, ["configurations", "vaccineMethods", "tanks"]))
            : _createCommentVNode("", true)
        ])),
    (!isLoading.value)
      ? (_openBlock(), _createBlock(FooterActions, { key: 2 }, {
          start: _withCtx(() => [
            _createElementVNode("button", {
              onClick: toggleInstructionsModal,
              class: "w-full"
            }, " Instructions ")
          ]),
          end: _withCtx(() => [
            (!isBathTask.value && hasConfigurations.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  onClick: handleProceed,
                  class: "w-full"
                }, " Proceed "))
              : (isBathTask.value && hasConfigurations.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("button", {
                      onClick: handleFinishTask,
                      disabled: isFinishing.value,
                      class: _normalizeClass(["w-full relative", { 'opacity-50 cursor-not-allowed': isFinishing.value }])
                    }, [
                      (isFinishing.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_13, _cache[10] || (_cache[10] = [
                            _createElementVNode("svg", {
                              class: "animate-spin -ml-1 mr-2 h-4 w-4 text-white",
                              xmlns: "http://www.w3.org/2000/svg",
                              fill: "none",
                              viewBox: "0 0 24 24"
                            }, [
                              _createElementVNode("circle", {
                                class: "opacity-25",
                                cx: "12",
                                cy: "12",
                                r: "10",
                                stroke: "currentColor",
                                "stroke-width": "4"
                              }),
                              _createElementVNode("path", {
                                class: "opacity-75",
                                fill: "currentColor",
                                d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              })
                            ], -1),
                            _createTextVNode(" Finishing... ")
                          ])))
                        : (_openBlock(), _createElementBlock("span", _hoisted_14, " Finish Task "))
                    ], 10, _hoisted_12)
                  ]))
                : (!hasConfigurations.value)
                  ? (_openBlock(), _createElementBlock("button", _hoisted_15, " Waiting for Configurations "))
                  : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (weightModal.value)
      ? (_openBlock(), _createBlock(WeightModal, {
          key: 3,
          show: weightModal.value,
          configuration: selectedConfiguration.value,
          onClose: _cache[2] || (_cache[2] = ($event: any) => (weightModal.value = false))
        }, null, 8, ["show", "configuration"]))
      : _createCommentVNode("", true),
    _createVNode(TaskCompletionBlockedModal, {
      show: completionBlockModal.value,
      reason: completionValidation.value.reason,
      onClose: _cache[3] || (_cache[3] = ($event: any) => (completionBlockModal.value = false))
    }, null, 8, ["show", "reason"])
  ]))
}
}

})